/**
 * FrontChannelSelect - Front Channel selection combobox with safeguards
 *
 * Features:
 * 1. Renders channel name (not just ID) in the UI
 * 2. Confirmation dialog when changing the value (affects all outgoing email)
 * 3. Warning when selecting an @finchlegal.com channel
 * 4. Warning when another firm already uses the same channel
 */
import { AlertTriangle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { useFirmsListQuery } from '@/services/api/generated/firmsService';

import { FirmFormValues } from '../firmFormSchema';
import { FirmFieldRow } from '../layout/FirmLayout';
import { FrontChannelOption, NONE_OPTION_ID } from '../types';

interface FrontChannelSelectProps {
  control: Control<FirmFormValues>;
  channels: Array<{
    id: string;
    name?: string | null;
    address?: string | null;
  }>;
  isLoading: boolean;
  /** The current firm's ID (used to exclude it from duplicate-channel checks) */
  currentFirmId?: string;
}

/** Check whether an address belongs to the finchlegal.com domain */
const isFinchlegalAddress = (address: string | null | undefined): boolean => {
  if (!address) return false;
  return /@finchlegal\.com$/i.test(address.trim());
};

export const FrontChannelSelect = ({
  control,
  channels,
  isLoading,
  currentFirmId,
}: FrontChannelSelectProps) => {
  const [pendingValue, setPendingValue] = useState<FrontChannelOption | null>(
    null,
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [fieldOnChange, setFieldOnChange] = useState<
    ((value: string) => void) | null
  >(null);

  // Fetch all firms to detect duplicate channel usage
  const { data: allFirms } = useFirmsListQuery();

  const noneOption: FrontChannelOption = {
    id: NONE_OPTION_ID,
    name: 'None',
    address: '',
  };

  const options: FrontChannelOption[] = useMemo(
    () => [
      noneOption,
      ...channels.map((c) => ({
        id: c.id,
        name: c.name || c.address || c.id,
        address: c.address || '',
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels],
  );

  /** Find firms (other than the current one) that use the given channel ID */
  const getFirmsUsingChannel = useCallback(
    (channelFrontId: string) => {
      if (!allFirms || !channelFrontId || channelFrontId === NONE_OPTION_ID)
        return [];
      return allFirms.filter(
        (f) =>
          f.id !== currentFirmId &&
          f.front_channel &&
          (f.front_channel as { front_id?: string }).front_id ===
            channelFrontId,
      );
    },
    [allFirms, currentFirmId],
  );

  /** Build warning messages for a given channel option */
  const getWarnings = useCallback(
    (option: FrontChannelOption) => {
      const warnings: string[] = [];

      if (option.id === NONE_OPTION_ID) return warnings;

      // Warn if channel address is @finchlegal.com
      if (isFinchlegalAddress(option.address)) {
        warnings.push(
          `This channel uses an @finchlegal.com address (${option.address}). ` +
            `Selecting it will cause outgoing emails for this firm to be sent from a Finch internal address rather than the firm's own address.`,
        );
      }

      // Warn if another firm uses the same channel
      const otherFirms = getFirmsUsingChannel(option.id);
      if (otherFirms.length > 0) {
        const firmNames = otherFirms.map((f) => f.name).join(', ');
        warnings.push(
          `This channel is already assigned to: ${firmNames}. ` +
            `Assigning the same channel to multiple firms may cause emails to be sent from the wrong address.`,
        );
      }

      return warnings;
    },
    [getFirmsUsingChannel],
  );

  /** Handle the user selecting a new channel - show confirmation dialog */
  const handleChannelChange = useCallback(
    (val: FrontChannelOption | null, onChange: (value: string) => void) => {
      const selected = val && val.id !== NONE_OPTION_ID ? val : noneOption;
      setPendingValue(selected);
      setFieldOnChange(() => onChange);
      setShowConfirmDialog(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Confirm the pending change */
  const confirmChange = useCallback(() => {
    if (fieldOnChange && pendingValue) {
      fieldOnChange(
        pendingValue.id === NONE_OPTION_ID ? '' : pendingValue.id,
      );
    }
    setShowConfirmDialog(false);
    setPendingValue(null);
    setFieldOnChange(null);
  }, [fieldOnChange, pendingValue]);

  /** Cancel the pending change */
  const cancelChange = useCallback(() => {
    setShowConfirmDialog(false);
    setPendingValue(null);
    setFieldOnChange(null);
  }, []);

  const pendingWarnings = pendingValue ? getWarnings(pendingValue) : [];

  return (
    <>
      <FirmFieldRow label="Front Channel" required={false}>
        <FormField
          control={control}
          name="front_channel_front_id"
          render={({ field }) => {
            const selected =
              options.find((o) => o.id === (field.value || NONE_OPTION_ID)) ||
              noneOption;

            // Inline warnings for the currently saved value
            const currentWarnings =
              selected.id !== NONE_OPTION_ID ? getWarnings(selected) : [];

            return (
              <FormItem>
                <FormControl>
                  <GroupedCombobox<FrontChannelOption>
                    label={null as unknown as string}
                    groups={[{ label: 'Channels', options }]}
                    value={selected}
                    onChange={(val) =>
                      handleChannelChange(val, field.onChange)
                    }
                    placeholder={
                      isLoading
                        ? 'Loading channels...'
                        : 'Select a Front channel'
                    }
                    emptyText="No channels found"
                    displayValue={(item) =>
                      `${item.name || ''} ${item.address || ''}`
                    }
                    renderItem={({ item }) => (
                      <div className="flex flex-col">
                        <div className="text-sm">
                          {item.id === NONE_OPTION_ID
                            ? 'None'
                            : (item.address || item.name)}
                        </div>
                      </div>
                    )}
                    renderSelection={({ item }) => (
                      <div className="flex items-center gap-2">
                        <span>
                          {item.id === NONE_OPTION_ID
                            ? 'None'
                            : (item.address || item.name)}
                        </span>
                      </div>
                    )}
                  />
                </FormControl>
                {/* Inline warnings for current value */}
                {currentWarnings.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {currentWarnings.map((warning, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800"
                      >
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </FirmFieldRow>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Front Channel?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Changing the Front Channel will affect{' '}
                  <strong>all outgoing email</strong> sent on behalf of this
                  firm. Please make sure the selected channel is correct.
                </p>
                {pendingValue && pendingValue.id !== NONE_OPTION_ID && (
                  <p className="font-medium">
                    New channel:{' '}
                    {pendingValue.address || pendingValue.name}
                  </p>
                )}
                {pendingValue && pendingValue.id === NONE_OPTION_ID && (
                  <p className="font-medium">
                    This will remove the Front Channel assignment (set to None).
                  </p>
                )}
                {pendingWarnings.length > 0 && (
                  <div className="space-y-2">
                    {pendingWarnings.map((warning, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-sm text-amber-800"
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmChange}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
